import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { task, didCancel } from 'ember-concurrency'
import { difference } from 'lodash'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type { IntlService } from 'ember-intl'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type Features from 'teacher-ui-client/services/features'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import type TeacherSubscription from 'teacher-ui-client/models/teacher-subscription'
import { FormField } from 'teacher-ui-client/components/form-field'
import { TeacherSelect } from 'teacher-ui-client/components/base/select/teacher'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { UIToggle } from 'teacher-ui-client/components/ui-toggle'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { LayoutBlocksConfirmModal } from 'teacher-ui-client/components/layout-blocks/confirm-modal'
import { on } from '@ember/modifier'
import { fn, uniqueId } from '@ember/helper'
import { t } from 'ember-intl'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { or, not, eq } from 'ember-truth-helpers'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import type { BufferedChangeset } from 'ember-changeset/types'
import { PhosphorIcon } from 'teacher-ui-client/components/phosphor-icon'

interface Args {
  classChangeset: BufferedChangeset & SchoolClass
  subscriptionType: SubscriptionType
  teachers?: Teacher[]
  onSchoolClassRemovedTeachers?: (schoolClass?: SchoolClass) => unknown
  usesExternalRostering?: boolean
}

interface Signature {
  Args: Args
  Element: HTMLFormElement
}

export class FormEditClassComponent extends Component<Signature> {
  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service resourceConflict!: ResourceConflictService

  @service featureControl!: FeatureControlService

  @service features!: Features

  @tracked selectedTeacherId?: string

  @tracked teacherToRemove?: Teacher

  @tracked showRemoveTeacherModal = false

  @tracked errorHandler!: ErrorHandler

  get allTeachers() {
    return this.args.teachers ?? []
  }

  get showMathseedsPrimeAccess() {
    return (
      this.args.subscriptionType === SubscriptionType.Maths &&
      this.features.isEnabled('tui_ms_prime_access') &&
      this.featureControl.canAccess('msp_product')
    )
  }

  get showPlayroomGamesAccess() {
    return (
      this.args.subscriptionType === SubscriptionType.Reading || this.args.subscriptionType === SubscriptionType.Maths
    )
  }

  /**
   * Returns true if the class already contains a trial teacher.
   *
   * This is used to disable the "add teacher to class" functionality, as a teacher on trial is not allowed to have
   * other teachers in their class (unless they are first removed from it).
   *
   * Additionally, a trial teacher cannot be added to a class (this logic is handled with via the teacher options). A
   * trial teacher can only be teaching a class if the class was created with that trial teacher selected.
   */
  get isTrialTeacherInClass(): boolean {
    return this.args.classChangeset
      .get('teachers')
      .some((t: Teacher) =>
        t.teacherSubscriptions.some(
          (teacherSub: TeacherSubscription) =>
            teacherSub.subscriptionType === this.args.subscriptionType && !teacherSub.isExpired,
        ),
      )
  }

  /**
   * Teacher options for the add teacher to class dropdown. Excludes teachers already in the class, or if trial
   */
  get teachersToAdd() {
    const teachersInClass = this.args.classChangeset.get('teachers')
    const availableTeachers = this.allTeachers.filter((teacher) => {
      const trialSubscriptions = teacher.teacherSubscriptions.filter((teacherSub) => {
        return teacherSub.subscriptionType === this.args.subscriptionType && !teacherSub.isExpired
      })
      const hasTrialSubscription = trialSubscriptions.length > 0
      return !teachersInClass.includes(teacher) && !hasTrialSubscription
    })

    return availableTeachers.sort((a, b) => a.fullName.localeCompare(b.fullName))
  }

  saveClass = task({ drop: true }, async (changeset) => {
    await changeset.save()
  })

  @action
  private rollback() {
    if (this.args.classChangeset.isDirty && this.saveClass.isIdle) {
      this.args.classChangeset.rollback()
    }
  }

  @action
  onSelectTeacher(teacherId: string) {
    this.selectedTeacherId = teacherId
  }

  /**
   * Remove the teacher from the class teachers. If it's the last teacher, instead display the confirmation modal
   */
  @action
  onRemoveTeacher(teacher: Teacher) {
    this.teacherToRemove = teacher
    if (this.args.classChangeset.get('teachers').length <= 1) {
      // last teacher, show confirmation modal
      this.showRemoveTeacherModal = true
    } else {
      // skip the modal, remove the teacher
      this.removeTeacherFromClass()
    }
  }

  /**
   * Adds a teacher to the class changeset
   *
   * As of writing, when dealing with relationships ember-changeset is only able to correctly handle set() calls.
   * If you addObject/removeObject from the changeset array, it will not mark the changeset as dirty.
   */
  @action
  addTeacherToClass() {
    const teacherToAdd = this.allTeachers.find((teacher) => teacher.id === this.selectedTeacherId)
    if (!teacherToAdd) return

    const teachers = (this.args.classChangeset.get('teachers') as Teacher[]).slice()
    const updatedTeachers = [...teachers, teacherToAdd]
    this.args.classChangeset.set('teachers', updatedTeachers) // set changeset to use new array
    this.selectedTeacherId = undefined

    this.verifyTeachersDirty()
  }

  /**
   * Remove teacher from the class changeset
   */
  @action
  removeTeacherFromClass() {
    const { teacherToRemove } = this
    if (teacherToRemove) {
      const teachers = (this.args.classChangeset.get('teachers') as Teacher[]).slice()
      const updatedTeachers = teachers.filter((teacher) => teacherToRemove.id !== teacher.id)
      this.args.classChangeset.set('teachers', updatedTeachers) // set changeset to use new array
      this.teacherToRemove = undefined

      this.verifyTeachersDirty()
    }
  }

  @action
  confirmRemoveTeacher() {
    this.removeTeacherFromClass()
    this.showRemoveTeacherModal = false
  }

  @action
  cancelRemoveTeacher() {
    this.teacherToRemove = undefined
    this.showRemoveTeacherModal = false
  }

  /**
   * This action checks the original teachers ID list against the updated one in the changeset. If the ids are all the
   * same (ignoring order) this will rollback the teachers property.
   *
   * This is to handle shortcomings of ember-changeset around relationships, where if you add a teacher then remove that
   * same teacher, the changeset thinks the property is still dirty, even when the order is the same.
   *
   * (This is likely due to the fact that when adding/removing teachers we (currently) need to set a new array - so it
   * ends up being a new array even if the teachers in it are the same, and marked as dirty)
   */
  @action
  verifyTeachersDirty() {
    const originalTeacherIds = this.args.classChangeset.get('data.teachers').map((teacher: Teacher) => teacher.id)
    const changesetTeacherIds = this.args.classChangeset.get('teachers').map((teacher: Teacher) => teacher.id)

    if (
      difference(originalTeacherIds, changesetTeacherIds).length === 0 &&
      difference(changesetTeacherIds, originalTeacherIds).length === 0
    ) {
      this.args.classChangeset.rollbackProperty('teachers')
    }
  }

  @action
  toggleMathseedsPrime() {
    this.args.classChangeset.set('mathseedsPrimeAccess', !this.args.classChangeset.get('mathseedsPrimeAccess'))
  }

  @action
  toggleGames() {
    this.args.classChangeset.set('gamesAccess', !this.args.classChangeset.get('gamesAccess'))
  }

  @action
  togglePlayroom() {
    this.args.classChangeset.set('playroomAccess', !this.args.classChangeset.get('playroomAccess'))
  }

  @action
  toggleClassMusic() {
    this.args.classChangeset.set('backgroundMusicDisabled', !this.args.classChangeset.get('backgroundMusicDisabled'))
  }

  @action
  onReset(event: Event) {
    event.preventDefault()
    this.rollback()
  }

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    const { classChangeset } = this.args
    const name = classChangeset.get('name')

    try {
      await this.saveClass.perform(classChangeset)

      // If no teachers left in class, the class will be automatically deleted by the backend.
      // We'll let the controller know and it can unload it from the store after leaving this page
      const schoolClass = classChangeset.get('data')
      const numTeachers = schoolClass.get('teachers').length ?? 0
      if (numTeachers === 0) {
        const deleteMessage = this.intl.t('manage.manageClasses.classes.deleteClass.success', { name })
        this.flashQueue.addSuccess({ subtitle: deleteMessage })
        this.args.onSchoolClassRemovedTeachers?.(schoolClass)
      } else {
        const updateMessage = this.intl.t('manage.manageClasses.classes.editClass.success', { name })
        this.flashQueue.addSuccess({ subtitle: updateMessage })
      }
    } catch (e: any) {
      if (!didCancel(e)) {
        const schoolClass = classChangeset.get('data')
        schoolClass.rollbackAttributes()

        if (isConflictError(e)) return this.resourceConflict.handleError('Resource Conflict', e)

        const message = this.intl.t('manage.manageClasses.classes.editClass.error', { name })
        this.flashQueue.addFail({ subtitle: message })
      }
    }
  }

  <template>
    <form
      data-test-edit-class
      class="space-y-3"
      autocomplete="off"
      {{on "submit" this.onSubmit}}
      {{on "reset" this.onReset}}
      ...attributes
    >
      <div class="font-semibold">{{t "manage.manageClasses.classes.editClass.editDetails"}}</div>
      <div class="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-cols-5 lg:grid-rows-3">
        <FormField
          class="lg:col-span-2"
          @changeset={{@classChangeset}}
          @propertyName="name"
          @labelText={{t "manage.manageClasses.classes.editClass.className"}}
          as |field|
        >
          <field.label class="font-normal" />
          <field.input data-test-class-name disabled={{@usesExternalRostering}} />
        </FormField>
        {{#let (uniqueId) as |uniqueId|}}
          {{#unless @usesExternalRostering}}
            <div class="lg:col-span-2 lg:row-span-2">
              <label for="add-teacher-{{uniqueId}}" class="font-normal">
                {{t "manage.manageClasses.classes.editClass.addTeacherTitle"}}
              </label>
              <div id="add-teacher-{{uniqueId}}" data-test-add-teacher class="flex gap-2">
                <TeacherSelect
                  class="min-w-0 grow basis-0 truncate"
                  disabled={{this.isTrialTeacherInClass}}
                  @placeholder={{t "manage.manageClasses.classes.editClass.addTeacherPlaceholder"}}
                  @teachers={{this.teachersToAdd}}
                  @teacherId={{this.selectedTeacherId}}
                  @updateTeacherId={{this.onSelectTeacher}}
                  @themeKey={{themeKey @subscriptionType}}
                />
                <UiButton
                  data-test-add-selected-teacher
                  disabled={{or (not this.selectedTeacherId) this.isTrialTeacherInClass}}
                  @appearance={{ButtonAppearance.muted}}
                  {{on "click" this.addTeacherToClass}}
                >
                  {{t "manage.manageClasses.classes.actions.addTeacher"}}
                </UiButton>
                {{#if this.isTrialTeacherInClass}}
                  <Tooltip @text={{t "manage.manageClasses.classes.editClass.addMultipleTeachersTooltip"}} />
                {{/if}}
              </div>
            </div>
          {{/unless}}
          <div class="lg:col-span-2 lg:row-span-3">
            <label for="remove-teacher-{{uniqueId}}">
              {{t "manage.manageClasses.classes.editClass.listTeachersTitle"}}
            </label>
            <ul id="remove-teacher-{{uniqueId}}" data-test-teachers-list>
              {{#each @classChangeset.teachers as |teacher index|}}
                <li
                  data-test-teacher-index={{index}}
                  class="bg-juicy-orange-100 flex justify-between p-1 even:bg-white"
                >
                  {{teacher.fullName}}
                  {{#unless @usesExternalRostering}}
                    <button
                      data-test-class-remove-teacher
                      type="button"
                      {{on "click" (fn this.onRemoveTeacher teacher)}}
                    >
                      <PhosphorIcon @icon="phosphor-trash-fill" class="h-5 w-5" />
                      {{t "manage.manageClasses.classes.actions.removeTeacher"}}
                    </button>
                  {{/unless}}
                </li>
              {{else}}
                <li>{{t "manage.manageClasses.classes.editClass.noTeachers"}}</li>
              {{/each}}
            </ul>
          </div>
          <div class="space-y-3 lg:col-span-1 lg:row-span-3">
            {{#if this.showMathseedsPrimeAccess}}
              <div>
                <label for="mathseeds-prime-access-{{uniqueId}}">
                  {{t "manage.manageClasses.classes.editClass.mspLabel"}}
                </label>
                <UIToggle
                  data-test-class-mathseeds-prime-toggle
                  id="mathseeds-prime-{{uniqueId}}"
                  @activeLabel={{t "manage.manageClasses.classes.editClass.toggleOn"}}
                  @inactiveLabel={{t "manage.manageClasses.classes.editClass.toggleOff"}}
                  @value={{@classChangeset.mathseedsPrimeAccess}}
                  @onToggle={{this.toggleMathseedsPrime}}
                  @themeKey={{themeKey @subscriptionType}}
                />
              </div>
            {{/if}}
            {{#if this.showPlayroomGamesAccess}}
              <div>
                <label for="games-access-{{uniqueId}}">
                  {{t "manage.manageClasses.classes.editClass.gamesLabel"}}
                </label>
                <UIToggle
                  data-test-class-games-toggle
                  id="games-access-{{uniqueId}}"
                  @activeLabel={{t "manage.manageClasses.classes.editClass.toggleOn"}}
                  @inactiveLabel={{t "manage.manageClasses.classes.editClass.toggleOff"}}
                  @value={{@classChangeset.gamesAccess}}
                  @onToggle={{this.toggleGames}}
                  @themeKey={{themeKey @subscriptionType}}
                />
              </div>
              <div>
                <label for="playroom-access-{{uniqueId}}">
                  {{t "manage.manageClasses.classes.editClass.playroomLabel"}}
                </label>
                <UIToggle
                  data-test-class-playroom-toggle
                  id="playroom-access-{{uniqueId}}"
                  @activeLabel={{t "manage.manageClasses.classes.editClass.toggleOn"}}
                  @inactiveLabel={{t "manage.manageClasses.classes.editClass.toggleOff"}}
                  @value={{@classChangeset.playroomAccess}}
                  @onToggle={{this.togglePlayroom}}
                  @themeKey={{themeKey @subscriptionType}}
                />
              </div>
            {{/if}}
            {{#if (eq @subscriptionType SubscriptionType.Reading)}}
              <div>
                <label for="readingeggs-class-music-{{uniqueId}}">
                  {{t "manage.manageClasses.classes.editClass.readingClassMusicLabel"}}
                </label>
                <span>
                  <PhosphorIcon @icon="phosphor-info-fill" /><Tooltip
                    @text={{t "manage.manageClasses.classes.editClass.readingClassMusicTooltip"}}
                  />
                </span>

                <UIToggle
                  data-test-class-background-music-toggle
                  id="readingeggs-class-music-{{uniqueId}}"
                  @activeLabel={{t "manage.manageClasses.classes.editClass.toggleOn"}}
                  @inactiveLabel={{t "manage.manageClasses.classes.editClass.toggleOff"}}
                  @value={{not @classChangeset.backgroundMusicDisabled}}
                  @onToggle={{this.toggleClassMusic}}
                  @themeKey={{themeKey @subscriptionType}}
                />
              </div>
            {{/if}}
            {{#if (eq @subscriptionType SubscriptionType.Maths)}}
              <div>
                <label for="mathseeds-class-music-{{uniqueId}}">
                  {{t "manage.manageClasses.classes.editClass.msClassMusicLabel"}}
                </label>
                <span>
                  <PhosphorIcon @icon="phosphor-info-fill" /><Tooltip
                    @text={{t "manage.manageClasses.classes.editClass.msClassMusicTooltip"}}
                  />
                </span>

                <UIToggle
                  data-test-class-background-music-toggle
                  id="mathseeds-class-music-{{uniqueId}}"
                  @activeLabel={{t "manage.manageClasses.classes.editClass.toggleOn"}}
                  @inactiveLabel={{t "manage.manageClasses.classes.editClass.toggleOff"}}
                  @value={{not @classChangeset.backgroundMusicDisabled}}
                  @onToggle={{this.toggleClassMusic}}
                  @themeKey={{themeKey @subscriptionType}}
                />
              </div>
            {{/if}}
          </div>
        {{/let}}
      </div>
      <div class="text-center">
        <UiButton
          data-test-edit-class-reset
          disabled={{@classChangeset.isPristine}}
          @appearance={{ButtonAppearance.muted}}
          type="reset"
        >
          {{t "manage.manageClasses.classes.actions.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-edit-class-submit
          @disabled={{or @classChangeset.isPristine @classChangeset.isInvalid}}
          @appearance={{ButtonAppearance.regular}}
          @defaultText={{t "manage.manageClasses.classes.actions.saveChanges"}}
          @state={{submitButtonState this.saveClass.last this.errorHandler}}
        />
      </div>
    </form>
    <LayoutBlocksConfirmModal
      @title={{t "manage.manageClasses.classes.removeTeacher.title"}}
      @showModal={{this.showRemoveTeacherModal}}
      @cancel={{this.cancelRemoveTeacher}}
      @confirm={{this.confirmRemoveTeacher}}
      @cancelButtonText={{t "manage.manageClasses.classes.removeTeacher.cancel"}}
      @confirmButtonText={{t "manage.manageClasses.classes.removeTeacher.ok"}}
    >
      <div class="space-y-3" data-test-remove-teacher-confirmation>
        <p>
          {{t
            "manage.manageClasses.classes.removeTeacher.confirmationLastTeacher"
            teacherName=this.teacherToRemove.fullName
            schoolClassName=@classChangeset.name
            htmlSafe=true
          }}
        </p>
        <p>
          {{t
            "manage.manageClasses.classes.removeTeacher.confirmation"
            teacherName=this.teacherToRemove.fullName
            schoolClassName=@classChangeset.name
            htmlSafe=true
          }}
        </p>
      </div>
    </LayoutBlocksConfirmModal>
  </template>
}

export default FormEditClassComponent
